/**
 * useFloorPlanProductFacts — resolves the product-derived facts the configurator
 * needs (`is3DAllowed`, `isEditing`) from the PF-v2 cart item + catalogue, NOT the
 * legacy products context (spec §8 / RISK-D / OQ-5).
 *
 * Legacy sourced these from `usePurchaseFlowProducts`/`usePurchaseFlowConfig`:
 *   - `is3DAllowed`  — no `MATTERPORT_FLOOR_PLAN_WITH_VISIT` kind selected.
 *   - `selectedCategory` editing-vs-real-estate — drove label/message copy.
 *   - `hasFloorPlanProduct` — always true here (this flow is only active for a
 *     floor-plan-config product), so the logo card always renders.
 *
 * v2 resolution (heuristic — OQ-5, confirm with product/BE at run end):
 *   - `is3DAllowed` ← not a Matterport floor plan, AND the variant offers the
 *     priced 3D extra: without it a 3D plan would be delivered and billed as 2D.
 *   - `isEditing`   ← the line's catalogue category is EDITING, as legacy keyed it on the order category.
 */

import { useMemo } from 'react'

import { ProductKind } from 'constants/product'
import { EDITING_CATEGORY_ID } from 'dataQueries/purchaseFlowRevampCatalogue.transform'
import type { CartItemId, InputImagePolicyV2, ResolvedShellProduct, ResolvedVariant, SalesforceId } from 'models/purchaseFlowRevamp'

import { useCartLineCatalogueEntry } from '../useCartLineCatalogueEntry'

export interface FloorPlanProductFacts {
  /** Whether the 3D render-type option is offered. */
  is3DAllowed: boolean
  /** Whether the line's variant has resolved from the catalogue. */
  isCatalogueResolved: boolean
  /** Whether this is a floor-plan-EDITING flow (vs basic real-estate capture). */
  isEditing: boolean
  /**
   * The chargeable 3D-upgrade extra offered with the selected variant, or null
   * when the catalogue offers none. Picking 3D in the configurator attaches it;
   * see `useFloorPlan3DExtraSync`.
   */
  threeDExtraId: SalesforceId | null
  /**
   * Whether the client has to hand over a source plan for this line. A catalogue
   * fact, not a flow one: `2D_FLOOR_PLAN_EDITING` is `UNLIMITED` (needs one) while
   * `MOBILE_APPLICATION_FLOOR_PLAN` is `NONE` — same configuration flow, opposite
   * answers, which is why the upload cannot be gated from the flow's DTO alone.
   */
  requiresSource: boolean
  /** The selected variant's `inputImagePolicy`, which decides whether its quantity follows its files. */
  inputImagePolicy: InputImagePolicyV2 | undefined
}

export function useFloorPlanProductFacts(cartItemId: CartItemId): FloorPlanProductFacts {
  const { shell, variant } = useCartLineCatalogueEntry(cartItemId)

  return useMemo(() => floorPlanProductFacts(shell, variant), [shell, variant])
}

/** The facts for one line's catalogue product and variant; nulls while the catalogue loads. */
export const floorPlanProductFacts = (
  shell: ResolvedShellProduct | null,
  variant: ResolvedVariant | null
): FloorPlanProductFacts => {
  const threeDExtraId = variant?.extras.find(
    (e) => e.productKind === ProductKind.FLOOR_PLAN_EDITING_3D
  )?.id ?? null

  return {
    is3DAllowed: shell?.kind !== ProductKind.MATTERPORT_FLOOR_PLAN_WITH_VISIT && threeDExtraId !== null,
    isCatalogueResolved: !!variant,
    isEditing: shell?.categoryId === EDITING_CATEGORY_ID,
    threeDExtraId,
    requiresSource: !!variant?.inputImagePolicy && variant.inputImagePolicy !== 'NONE',
    inputImagePolicy: variant?.inputImagePolicy as InputImagePolicyV2 | undefined,
  }
}
